/**
 * Explore Collections carousel.
 *
 * Owns four things the CSS cannot: pointer drag-to-scroll, the arrow buttons,
 * the "02 / 06" counter, and the progress bar. Scrolling itself stays native,
 * so keyboard, trackpad and touch all keep working untouched.
 */
class NbExplore extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  #raf = 0;
  #snapTimer = 0;

  get viewport() {
    return this.querySelector('[data-nb-xc-viewport]');
  }

  connectedCallback() {
    // A fresh controller per connection. Moving the element in the DOM
    // disconnects and reconnects it, and a controller aborted by the previous
    // disconnect would silently drop every listener registered with its signal
    // — leaving the arrows, counter and progress bar inert with no error.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const viewport = this.viewport;
    if (!viewport) return;

    viewport.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });

    this.querySelector('[data-nb-xc-prev]')?.addEventListener('click', () => this.#page(-1), { signal });
    this.querySelector('[data-nb-xc-next]')?.addEventListener('click', () => this.#page(1), { signal });

    if (this.hasAttribute('draggable-scroll')) this.#enableDrag(signal);

    // Set the counter and arrow states synchronously. A background tab never
    // runs animation frames, so deferring this entirely would leave the
    // controls stale until the tab is first brought to the front.
    this.#render();
    // Measure again after a frame, once lazy-loaded cards have their real width.
    requestAnimationFrame(this.#onScroll);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#raf);
    clearTimeout(this.#snapTimer);
  }

  /** Width of one card plus the gap — the distance one arrow press travels. */
  get #step() {
    const card = this.querySelector('.nb-xc__card');
    if (!card) return 0;
    const track = this.querySelector('.nb-xc__track');
    const gap = track ? parseFloat(getComputedStyle(track).columnGap) || 0 : 0;
    return card.getBoundingClientRect().width + gap;
  }

  /**
   * Scrolls by one card. When looping is on, running off either end wraps to
   * the opposite side instead of stopping.
   * @param {number} direction -1 for previous, 1 for next.
   */
  #page(direction) {
    const viewport = this.viewport;
    const step = this.#step;
    if (!viewport || !step) return;

    const max = viewport.scrollWidth - viewport.clientWidth;
    const target = viewport.scrollLeft + step * direction;

    if (this.hasAttribute('loop')) {
      // 1px of tolerance — scrollLeft is fractional on zoomed/HiDPI displays.
      if (target > max + 1) return this.#scrollTo(0);
      if (target < -1) return this.#scrollTo(max);
    }

    this.#scrollTo(Math.max(0, Math.min(target, max)));
  }

  /**
   * Scrolls the viewport, briefly suspending scroll snapping.
   *
   * Under `scroll-snap-type: mandatory` some browsers cancel a programmatic
   * smooth scroll and snap straight back to the starting card, which makes the
   * arrows look dead. Suspending snap for the duration avoids that, and since
   * the target is always a card boundary, restoring it shifts nothing.
   *
   * @param {number} left
   */
  #scrollTo(left) {
    const viewport = this.viewport;
    if (!viewport) return;

    const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const restore = viewport.style.scrollSnapType;

    viewport.style.scrollSnapType = 'none';
    viewport.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });

    clearTimeout(this.#snapTimer);
    this.#snapTimer = window.setTimeout(
      () => {
        viewport.style.scrollSnapType = restore;
      },
      smooth ? 600 : 0
    );
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#render());
  };

  #render() {
    const viewport = this.viewport;
    if (!viewport) return;

    const max = viewport.scrollWidth - viewport.clientWidth;
    const progress = max > 0 ? viewport.scrollLeft / max : 0;

    const bar = this.querySelector('[data-nb-xc-bar]');
    if (bar) {
      // The bar is a fixed-width thumb that travels the track, so it reads as a
      // scrollbar rather than a fill meter.
      const travel = 100 / (parseFloat(getComputedStyle(bar).width) / bar.parentElement.clientWidth) - 100;
      bar.style.transform = `translateX(${progress * travel}%)`;
    }

    const counter = this.querySelector('[data-nb-xc-current]');
    if (counter) {
      const step = this.#step;
      const total = this.querySelectorAll('.nb-xc__card').length;
      const index = step ? Math.round(viewport.scrollLeft / step) + 1 : 1;
      counter.textContent = this.#pad(Math.min(index, total));
    }

    const prev = this.querySelector('[data-nb-xc-prev]');
    const next = this.querySelector('[data-nb-xc-next]');
    if (!this.hasAttribute('loop')) {
      if (prev) prev.disabled = viewport.scrollLeft <= 1;
      if (next) next.disabled = viewport.scrollLeft >= max - 1;
    }
  }

  /** @param {number} n */
  #pad(n) {
    return this.hasAttribute('pad-zero') && n < 10 ? `0${n}` : String(n);
  }

  /** @param {AbortSignal} signal */
  #enableDrag(signal) {
    const viewport = this.viewport;
    if (!viewport) return;

    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let captured = false;
    let moved = 0;

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        // Let touch keep native momentum scrolling; only take over for mouse/pen.
        if (event.pointerType === 'touch' || event.button !== 0) return;
        dragging = true;
        captured = false;
        moved = 0;
        startX = event.clientX;
        startScroll = viewport.scrollLeft;
        // Deliberately no setPointerCapture here. Capturing on press retargets
        // the click to the viewport, so the card link underneath never
        // receives it and a plain click stops navigating. Capture is taken
        // below, only once the pointer has actually travelled.
      },
      { signal }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        // The button was released somewhere we could not see (outside the
        // window, over an iframe). Treat it as the end of the gesture rather
        // than scrolling with no button held.
        if ((event.buttons & 1) === 0) return end(event);

        const dx = event.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        // Only claim the gesture once it is clearly a drag, so small jitters on
        // a click still open the collection. Capturing here keeps pointerup
        // arriving even when the mouse leaves the carousel mid-drag, and
        // steers the trailing click to the viewport instead of a link.
        if (moved > 4 && !captured) {
          captured = true;
          viewport.setAttribute('data-dragging', '');
          try {
            viewport.setPointerCapture(event.pointerId);
          } catch {
            /* capture is a nicety; the window listeners below still end the drag */
          }
        }
        if (captured) viewport.scrollLeft = startScroll - dx;
      },
      { signal }
    );

    const end = (event) => {
      if (!dragging) return;
      dragging = false;
      if (captured && viewport.hasPointerCapture?.(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
      captured = false;
      // Drop the attribute after the click event has passed, otherwise the
      // pointer-events guard lifts too early and the drag opens a link.
      requestAnimationFrame(() => viewport.removeAttribute('data-dragging'));
    };

    // Bound on window so a release outside the carousel — before capture has
    // been taken — still ends the gesture instead of leaving it armed.
    window.addEventListener('pointerup', end, { signal });
    window.addEventListener('pointercancel', end, { signal });
    viewport.addEventListener('lostpointercapture', end, { signal });

    viewport.setAttribute('data-draggable', '');
  }
}

if (!customElements.get('nb-explore')) {
  customElements.define('nb-explore', NbExplore);
}
